<template>
  <div class="create-invoice-modal">
    <!-- Заголовок накладной -->
    <div class="invoice-header">
      <div class="form-row">
        <div class="form-field">
          <div class="field-label">Номер накладной *</div>
          <q-input
            v-model="form.invoice_num"
            outlined
            dense
            placeholder="Введите номер"
            :error="invoiceNumDuplicate"
            :error-message="invoiceNumDuplicate ? 'Накладная с таким номером уже существует у этого поставщика' : ''"
            :loading="invoiceNumChecking"
            :hide-bottom-space="!invoiceNumDuplicate"
          />
        </div>
        <div class="form-field">
          <div class="field-label">Дата накладной *</div>
          <q-input
            v-model="form.invoice_date"
            type="date"
            outlined
            dense
          />
        </div>
        <div class="form-field">
          <div class="field-label">С НДС</div>
          <q-toggle v-model="form.is_nds" />
        </div>
      </div>
    </div>

    <!-- Информация о заявке -->
    <div class="request-info" v-if="data.request">
      <q-chip color="primary" text-color="white" icon="apartment" :title="'ЖК: ' + data.request.project_name">
        {{ data.request.project_name }}
      </q-chip>
      <q-chip color="grey" text-color="white" icon="person" :title="'Поставщик: ' + (data.request.provider_name || 'Не указан')">
        {{ data.request.provider_name || 'Поставщик не указан' }}
      </q-chip>
    </div>

    <!-- Доверенность -->
    <div class="poa-section">
      <q-expansion-item
        v-model="poaExpanded"
        dense
        icon="assignment"
        label="Доверенность"
        caption="Укажите данные доверенности (необязательно)"
        class="poa-expansion"
      >
        <div class="poa-fields">
          <div class="poa-row">
            <div class="poa-field poa-field--num">
              <div class="field-label">Номер доверенности</div>
              <q-input
                v-model="form.power_of_attorney_num"
                outlined
                dense
                placeholder="Напр. 123/А"
              />
            </div>
            <div class="poa-field poa-field--date">
              <div class="field-label">Дата доверенности</div>
              <q-input
                v-model="form.power_of_attorney_date"
                type="date"
                outlined
                dense
              />
            </div>
          </div>
          <div class="poa-field">
            <div class="field-label">Кому выдана доверенность</div>
            <q-input
              v-model="form.power_of_attorney_issued_to"
              outlined
              dense
              placeholder="ФИО"
            />
          </div>
        </div>
      </q-expansion-item>
    </div>
    <DocumentUploader ref="documentUploader" :allowed-extensions="['.pdf']" accepted-formats="PDF"
        @file-selected="handleFileSelected" @file-removed="handleFileRemoved" />


    <!-- Таблица позиций -->
    <div class="items-section">
      <div class="section-header">
        <h4>Позиции заявки</h4>
      </div>

      <q-linear-progress v-if="loading" indeterminate />

      <q-table
        v-else
        :rows="items"
        :columns="columns"
        row-key="provider_request_item_id"
        flat
        dense
        :pagination="{ rowsPerPage: 0 }"
        hide-pagination
        class="items-table"
      >
        <template v-slot:body="props">
          <q-tr :props="props" :class="{ 'selected-row': isSelected(props.row) }">
            <q-td auto-width :title="props.row.remaining_cnt > 0 ? 'Выбрать для добавления в накладную' : 'Нет остатка'">
              <q-checkbox
                :model-value="isSelected(props.row)"
                @update:model-value="toggleSelect(props.row)"
                :disable="true"
              />
            </q-td>
            <q-td class="material-cell" :title="props.row.material_name">
              <div class="material-name-wrap clickable" @click.stop="openProviderMaterialDialog(props.row)">
                <div class="material-title">{{ props.row.material_name }}</div>
                <div class="material-provider-meta">
                  <div
                    class="meta-block meta-block--provider"
                    :class="{ 'meta-block--identical': isProviderNameIdentical(props.row) }"
                  >
                    <div class="meta-head">
                      <span class="meta-label">Название поставщика</span>
                    <q-badge
                      v-if="isProviderNameIdentical(props.row)"
                      color="green-7"
                      text-color="white"
                      class="meta-badge"
                      label="Идентично"
                    />
                  </div>
                    <div
                      v-if="!isProviderNameIdentical(props.row)"
                      class="meta-value meta-value--provider"
                    >
                      {{ props.row.provider_material_name || 'не указано' }}
                    </div>
                  </div>
                  <div class="meta-block meta-block--nomenklatura">
                    <div class="meta-head">
                      <span class="meta-label">Номенклатурный номер поставщика</span>
                    </div>
                    <div class="meta-value">
                      {{ props.row.provider_nomenklatur_num || 'не указан' }}
                    </div>
                  </div>
                </div>
              </div>
            </q-td>
            <q-td class="text-center" :title="'Количество в заявке: ' + props.row.material_cnt + ' ' + (props.row.material_unit_name || '')">
              {{ props.row.material_cnt }} <span class="unit-name">{{ props.row.material_unit_name }}</span>
            </q-td>
            <q-td class="text-right" :title="isSelected(props.row) ? 'Введите цену за единицу' : 'Цена за единицу: ' + formatAmount(props.row.price)">
              <q-input
                v-if="isSelected(props.row)"
                v-model.number="selectedPrices[props.row.provider_request_item_id]"
                type="number"
                dense
                outlined
                :min="0"
                style="width: 110px"
                hide-bottom-space
              />
              <span v-else>{{ formatAmount(props.row.price) }}</span>
            </q-td>
            <q-td class="text-center" :title="'Уже добавлено в накладные: ' + props.row.invoiced_cnt">
              <span class="invoiced">{{ props.row.invoiced_cnt }}</span>
            </q-td>
            <q-td class="text-center" :title="'Доступно для добавления: ' + props.row.remaining_cnt">
              <span :class="props.row.remaining_cnt > 0 ? 'remaining' : 'no-remaining'">
                {{ props.row.remaining_cnt }}
              </span>
            </q-td>
            <q-td class="text-center" :title="isSelected(props.row) ? 'Введите количество для накладной' : 'Выберите позицию'">
              <q-input
                v-if="isSelected(props.row)"
                v-model.number="selectedItems[props.row.provider_request_item_id]"
                type="number"
                dense
                outlined
                :disable="true"
                :min="0.01"
                style="width: 80px"
                :error="selectedItems[props.row.provider_request_item_id] > props.row.remaining_cnt"
                :error-message="`Макс: ${props.row.remaining_cnt}`"
              />
              <span v-else>-</span>
            </q-td>
          </q-tr>
        </template>
      </q-table>
    </div>

    <!-- Данные материала от поставщика -->
    <q-dialog v-model="providerMaterialDialog" persistent>
      <q-card style="min-width: 520px; max-width: 720px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Укажите от поставщика</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup :disable="providerMaterialSaving" />
        </q-card-section>

        <q-card-section>
          <div class="q-gutter-md">
            <q-input
              v-model="providerMaterialForm.provider_material_name"
              outlined
              dense
              label="Наименование материала от поставщика"
              :disable="providerMaterialSaving"
            />
            <q-input
              v-model="providerMaterialForm.provider_nomenklatur_num"
              outlined
              dense
              label="Номенклатурный номер"
              :disable="providerMaterialSaving"
            />
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" v-close-popup :disable="providerMaterialSaving" />
          <q-btn unelevated label="Сохранить" color="primary" @click="saveProviderMaterial" :loading="providerMaterialSaving" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Итого -->
    <div class="totals-section" v-if="selectedCount > 0">
      <div class="total-row">
        <span>Выбрано позиций:</span>
        <strong>{{ selectedCount }}</strong>
      </div>
      <div class="total-row">
        <span>Сумма:</span>
        <strong class="total-sum">{{ formatAmount(totalSum) }}</strong>
      </div>
    </div>

    <!-- Кнопки -->
    <q-card-actions align="right" class="q-mt-md">
      <q-btn flat label="Отмена" color="grey" @click="closeModal()" />
      <q-btn 
        unelevated 
        label="Создать накладную" 
        color="primary" 
        @click="submit"
        :loading="submitting"
        :disable="!canSubmit"
      />
    </q-card-actions>
  </div>
</template>

<script>
import DocumentUploader from "@/components/common/DocumentUploader";
import { getItemsForInvoice, uploadSignedInvoice, updateMaterialProvider, checkInvoiceNum } from "@/views/SupplierRequests/services";
import { useCustomSnackbar } from '@/_helpers/hooks/useCustomSnackbar'

export default {
  name: "UploadInvoice",
  components: { DocumentUploader },
  props: {
    data: {
      type: Object,
      required: true,
    },
    closeModal: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      poaExpanded: false,
      invoiceNumDuplicate: false,
      invoiceNumChecking: false,
      invoiceNumCheckTimer: null,
      providerMaterialDialog: false,
      providerMaterialSaving: false,
      providerMaterialRowKey: null,
      providerMaterialForm: {
        provider_id: null,
        material_id: null,
        provider_material_name: '',
        provider_nomenklatur_num: '',
      },
      items: [],
      selectedItems: {}, // { provider_request_item_id: qty }
      selectedPrices: {}, // { provider_request_item_id: price }
      form: {
        invoice_num: '',
        invoice_date: this.formatDateForInput(new Date()),
        is_nds: true,
        power_of_attorney_num: '',
        power_of_attorney_date: null,
        power_of_attorney_issued_to: '',
        file: null,
      },
      columns: [
        { name: 'select', label: '', align: 'center', style: 'width: 40px' },
        { name: 'material_name', label: 'Материал', field: 'material_name', align: 'left', style: 'max-width: 250px' },
        { name: 'material_cnt', label: 'Кол-во', field: 'material_cnt', align: 'center', style: 'width: 90px' },
        { name: 'price', label: 'Цена', field: 'price', align: 'right', style: 'width: 100px' },
        { name: 'invoiced_cnt', label: 'В накл.', field: 'invoiced_cnt', align: 'center', style: 'width: 80px' },
        { name: 'remaining_cnt', label: 'Остаток', field: 'remaining_cnt', align: 'center', style: 'width: 80px' },
        { name: 'qty', label: 'Добавить', align: 'center', style: 'width: 90px' },
      ],
    };
  },
  computed: {
    providerId() {
      return this.data?.request?.provider_id || null;
    },
    selectedCount() {
      return Object.keys(this.selectedItems).filter(k => this.selectedItems[k] > 0).length;
    },
    totalSum() {
      let sum = 0;
      for (const itemId in this.selectedItems) {
        const qty = this.selectedItems[itemId];
        if (qty > 0) {
          const item = this.items.find(i => i.provider_request_item_id === parseInt(itemId));
          if (item) {
            sum += qty * (this.selectedPrices[parseInt(itemId)] ?? item.price ?? 0);
          }
        }
      }
      return sum;
    },
    canSubmit() {
      if (!this.form.invoice_num || !this.form.invoice_date || this.selectedCount === 0
      //  || !this.form.file
      ) {
        return false;
      }
      if (this.invoiceNumDuplicate || this.invoiceNumChecking) {
        return false;
      }
      // Проверяем что все количества не превышают остаток
      for (const itemId in this.selectedItems) {
        const qty = this.selectedItems[itemId];
        if (qty > 0) {
          const item = this.items.find(i => i.provider_request_item_id === parseInt(itemId));
          if (item && qty > item.remaining_cnt) {
            return false;
          }
        }
      }
      return true;
    },
  },
  watch: {
    'form.invoice_num'(val) {
      this.invoiceNumDuplicate = false;
      clearTimeout(this.invoiceNumCheckTimer);
      const num = this.normalizeInvoiceNum(val);
      if (!num || !this.providerId) {
        this.invoiceNumChecking = false;
        return;
      }
      this.invoiceNumChecking = true;
      this.invoiceNumCheckTimer = setTimeout(() => this.checkInvoiceNumUnique(num), 500);
    },
  },
  methods: {
    normalizeInvoiceNum(val) {
      return (val || '').trim()
        .replace(/[^a-zA-Zа-яА-ЯёЁ0-9]+$/, '')
        .replace(/^[^a-zA-Zа-яА-ЯёЁ0-9]+/, '');
    },
    async checkInvoiceNumUnique(num) {
      try {
        const res = await checkInvoiceNum(num, this.providerId);
        if (this.normalizeInvoiceNum(this.form.invoice_num) === num) {
          this.invoiceNumDuplicate = res?.is_unique === false;
        }
      } catch (e) {
        // ignore
      } finally {
        this.invoiceNumChecking = false;
      }
    },
    normalizeText(value) {
      return (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
    },
    isProviderNameIdentical(row) {
      const a = this.normalizeText(row?.material_name);
      const b = this.normalizeText(row?.provider_material_name);
      return !!a && !!b && a === b;
    },
    formatDateForInput(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatAmount(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value) + ' ₸';
    },
    isSelected(row) {
      return this.selectedItems[row.provider_request_item_id] !== undefined;
    },
    toggleSelect(row) {
      return
    },
    handleFileSelected(file) {
      this.form.file = file
      this.fileError = ''
    },
    handleFileRemoved() {
      this.form.file = null
      this.fileError = ''
    },
    openProviderMaterialDialog(row) {
      const materialId = row?.material_id;
      const providerId = row?.provider_id;

      if (!materialId || !providerId) {
        this.$q.notify({
          type: 'negative',
          message: 'Не удалось определить material_id / provider_id для материала',
        });
        return;
      }

      this.providerMaterialRowKey = row?.provider_request_item_id ?? null;
      this.providerMaterialForm = {
        provider_id: providerId,
        material_id: materialId,
        provider_material_name: row?.provider_material_name || '',
        provider_nomenklatur_num: row?.provider_nomenklatur_num || '',
      };
      this.providerMaterialDialog = true;
    },
    async saveProviderMaterial() {
      const payload = {
        provider_id: this.providerMaterialForm.provider_id,
        material_id: this.providerMaterialForm.material_id,
        provider_material_name: (this.providerMaterialForm.provider_material_name || '').trim(),
        provider_nomenklatur_num: (this.providerMaterialForm.provider_nomenklatur_num || '').trim(),
      };

      if (!payload.provider_material_name) {
        this.$q.notify({ type: 'negative', message: 'Заполните "Наименование материала от поставщика"' });
        return;
      }

      this.providerMaterialSaving = true;
      try {
        await updateMaterialProvider(payload);

        // Обновляем локальную строку в таблице
        if (this.providerMaterialRowKey) {
          const idx = this.items.findIndex(i => i.provider_request_item_id === this.providerMaterialRowKey);
          if (idx !== -1) {
            const updated = {
              ...this.items[idx],
              provider_material_name: payload.provider_material_name,
              provider_nomenklatur_num: payload.provider_nomenklatur_num,
            };
            this.items.splice(idx, 1, updated);
          }
        }

        this.$q.notify({
          type: 'positive',
          message: 'Данные материала поставщика сохранены',
          timeout: 1500,
        });
        this.providerMaterialDialog = false;
      } catch (e) {
        this.$q.notify({
          type: 'negative',
          message: e?.response?.data?.error || 'Ошибка сохранения данных материала поставщика',
        });
      } finally {
        this.providerMaterialSaving = false;
      }
    },
    async loadItems() {
      this.loading = true;
      const providerRequestId = this.data.request?.provider_request_id;
      if (providerRequestId) {
        const res = await getItemsForInvoice(providerRequestId);
        this.items = res || [];
        if (this.items.length > 0) {
          this.items.forEach(item => {
            if (item.remaining_cnt > 0) {
              this.selectedItems[item.provider_request_item_id] = item.remaining_cnt;
              this.selectedPrices[item.provider_request_item_id] = item.price ?? 0;
            }
          });
          this.selectedItems = { ...this.selectedItems };
          this.selectedPrices = { ...this.selectedPrices };
        }
      }
      this.loading = false;
    },
    async submit() {
      if (!this.canSubmit) return;

      
      // if (!this.form.file) {
      //   this.showErrorSnackbar('Загрузите файл документа')
      //   return
      // }

      this.submitting = true;
      
      const itemsToCreate = [];
      for (const itemId in this.selectedItems) {
        const qty = this.selectedItems[itemId];
        if (qty > 0) {
          itemsToCreate.push({
            provider_request_item_id: parseInt(itemId),
            material_cnt: qty,
            price: this.selectedPrices[parseInt(itemId)] ?? 0
          });
        }
      }

      try {
        const formData = new FormData();
        formData.append('invoice_date', this.form.invoice_date);
        formData.append('invoice_num', this.form.invoice_num);
        formData.append('is_nds', this.form.is_nds ? 1 : 0);
        formData.append('power_of_attorney_num', (this.form.power_of_attorney_num || '').trim() || null);
        formData.append('power_of_attorney_date', this.form.power_of_attorney_date || null);
        formData.append('power_of_attorney_issued_to', (this.form.power_of_attorney_issued_to || '').trim() || null);
        formData.append('items', JSON.stringify(itemsToCreate));
        formData.append('file', this.form.file);
        const result = await uploadSignedInvoice(formData);

        if (result?.provider_request_invoice_id) {
          this.$q.notify({
            type: 'positive',
            message: `Накладная №${this.form.invoice_num} создана`,
          });
          
          if (this.data.onSuccess) {
            this.data.onSuccess(result);
          }
          
          this.closeModal();
        }
      } catch (e) {
        this.$q.notify({
          type: 'negative',
          message: e?.response?.data?.error || 'Ошибка создания накладной',
        });
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    this.loadItems();
  },
  beforeUnmount() {
    clearTimeout(this.invoiceNumCheckTimer);
  },
  setup() {
    const { showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar()
    return { showErrorSnackbar, showSucceedSnackbar }
  },
};
</script>

<style scoped lang="scss">
.create-invoice-modal {
  min-width: 700px;
  max-width: 900px;
  padding: 20px;
}

.invoice-header {
  margin-bottom: 20px;
}

.form-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
}

.request-info {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.poa-section {
  margin-bottom: 16px;
}

.poa-expansion {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.poa-fields {
  padding: 12px 16px 14px 16px;
}

.poa-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 12px;
}

.poa-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.poa-field--num {
  flex: 1;
  min-width: 240px;
}

.poa-field--date {
  width: 180px;
  min-width: 180px;
}

.items-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 15px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f5f5;

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
  }
}

.items-table {
  max-height: 400px;
  overflow-y: auto;
}

.material-cell {
  max-width: 250px;
}

.material-name-wrap {
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.3;
  font-size: 13px;
}

.clickable {
  cursor: pointer;
}

.material-title {
  font-size: 13px;
}

.material-provider-meta {
  margin-top: 6px;
  font-size: 11px;
  color: #6c757d;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.meta-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-block--identical {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-left: 4px solid #2e7d32;
  border-radius: 6px;
  padding: 6px 8px;
}

.meta-head {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.meta-label {
  color: #9e9e9e;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

.meta-value {
  color: #455a64;
  font-size: 12px;
  line-height: 1.25;
}

.meta-value--provider {
  color: #1565c0;
  font-weight: 600;
}

.meta-badge {
  font-size: 10px;
  padding: 2px 6px;
}

.selected-row {
  background: #e3f2fd !important;
}

.invoiced {
  color: #1976d2;
  font-weight: 500;
}

.remaining {
  color: #2e7d32;
  font-weight: 600;
}

.no-remaining {
  color: #9e9e9e;
}

.unit-name {
  font-size: 11px;
  color: #6c757d;
}

.totals-section {
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 8px;
}

.total-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.total-sum {
  color: #2e7d32;
  font-size: 16px;
}
</style>

